import { Temporal } from 'https://cdn.jsdelivr.net/npm/temporal-polyfill@0.3.2/+esm';

/** @type {import('../types').EstimatedDelivery} */
class EstimatedDelivery extends HTMLElement {

  settings;

  constructor() {
    super();
    this.holidays = [];

    this.countdownInterval = null;

    this.settings = this.parseDataset({});
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  /**
   * @param {Record<string,string|undefined> & {icon?: "truck"|"package"|"clock"}} dataset
   */
  parseDataset(dataset) {
    /** @type {import('../types').EstimatedDeliverySettings} */
    return {
      version: parseInt(dataset.version || '2'),
      showAlert: dataset.showAlert === 'true',
      alertText: dataset.alertText || 'Free Shipping',
      cutoffHour: parseInt(dataset.cutoffHour || '11'),
      cutoffMinute: parseInt(dataset.cutoffMinute || '00'),
      cutoffTimezone: dataset.cutoffTimezone || 'America/New_York',
      processingMin: parseInt(dataset.processingMin || '0'),
      processingMax: parseInt(dataset.processingMax || '1'),
      deliveryMin: parseInt(dataset.deliveryMin || '2'),
      deliveryMax: parseInt(dataset.deliveryMax || '3'),
      includeWeekends: dataset.includeWeekends === 'true',
      includeHolidays: dataset.includeHolidays === 'true',
      holidayUrl: dataset.holidayUrl || '',
      marketRules: this.parseMarketRules(dataset.marketRules || '[]'),
      messageTemplate: dataset.messageTemplate || 'Order by {cutoff_datetime} for delivery between {min_date} and {max_date}',
      icon:  dataset.icon || 'truck',
      showCountdown: dataset.showCountdown === 'true',
      countdownLabel: dataset.countdownLabel || 'Order within {time} to ship that day',
      showFlow: dataset.showFlow === 'true',
      storeLocale: dataset.storeLocale || 'en-US',
      todayLabel: dataset.todayLabel || 'Today'
    };
  }

  init() {
    this.settings = this.parseDataset(this.dataset);

    // Build initial DOM
    this.buildDOM();

    // Load holidays and calculate
    this.loadHolidays().then(() => {
      if (this.settings.version === 2) {
        this.updateVersion2();
      } else {
        this.updateDeliveryInfo();
        if (this.settings.showCountdown) {
          this.startCountdown();
        }
      }
    });

    // Listen for variant changes
    document.addEventListener('variant:change', (e) => {
      debugger;
      if (this.settings.version === 2) {
        this.updateVersion2();
      } else {
        this.updateDeliveryInfo();
      }
    });
  }

  /**
   * @param {string} iconSvg
   * @param {Record<string,string>} flowIcons
   * @returns {void}
   * @private
   */
  _buildVersion1(iconSvg, flowIcons) {
    const htmlVersion1 = /* html */ `
      <div class="delivery-widget">
        <div class="icon-and-text">
          <span class="delivery-widget__icon">${iconSvg}</span>
          <span class="text">
            ${this.settings.showCountdown ? '<div class="delivery-countdown"></div>' : ''}
            <div class="delivery-message"></div>
          </span>
        </div>
        ${this.settings.showFlow ? `
          <div class="delivery-flow">
            <div class="delivery-flow__steps">
              <div class="delivery-flow__step" data-step="ordered">
                <div class="delivery-flow__step-icon">${flowIcons.ordered}</div>
                <div class="delivery-flow__step-label">Ordered</div>
                <div class="delivery-flow__step-date"></div>
              </div>
              <div class="delivery-flow__step delivery-flow__step--active" data-step="processing">
                <div class="delivery-flow__step-icon">${flowIcons.processing}</div>
                <div class="delivery-flow__step-label">Processing</div>
                <div class="delivery-flow__step-date"></div>
              </div>
              <div class="delivery-flow__step" data-step="delivered">
                <div class="delivery-flow__step-icon">${flowIcons.delivered}</div>
                <div class="delivery-flow__step-label">Delivered</div>
                <div class="delivery-flow__step-date"></div>
              </div>
            </div>
          </div>
        ` : ''}
      </div>
    `;
    this.innerHTML = htmlVersion1;
    this.countdownElement = this.querySelector('.delivery-countdown');
    this.messageElement = this.querySelector('.delivery-message');
    this.flowSteps = this.querySelectorAll('.delivery-flow__step');
  }

  /**
   * @param {string} icon
   * @param {string} text
   */
  _buildVersion2(icon, text) {
    const htmlVersion2 = /* html */ `
<div class="delivery-widget delivery-widget--v2">
  <div class="title">Free 2-day air shipping in continental US</div>
  <div>Estimated arrival: <output class="estimated-arrival-output">Estimating...</output></div>
  ${this.settings.showAlert ? `
<div class="estimated-arrival-alert">
    <div class="estimated-arrival-alert--icon">${icon}</div>
    <div class="estimated-arrival-alert--text">${text}</div>
</div>` :''}`;
    this.innerHTML = htmlVersion2;
    this.widgetV2 = this.querySelector('.delivery-widget--v2');
    this.widgetV2Title = this.widgetV2?.querySelector('.title');
    this.widgetV2Output = this.widgetV2?.querySelector('.estimated-arrival-output');
    if (this.settings.showAlert) {
      this.widgetV2Alert = this.widgetV2?.querySelector('.estimated-arrival-alert');
      this.widgetV2AlertIcon = this.widgetV2Alert?.querySelector('.estimated-arrival-alert--icon');
      this.widgetV2AlertText = this.widgetV2Alert?.querySelector('.estimated-arrival-alert--text');
    }
  }

  buildDOM() {
    const icons = {
      truck: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-truck" viewBox="0 0 64 64"><defs><style>.a{stroke-miterlimit:10;stroke-width:2px;}</style></defs><path class="a" d="M16.5 43.22H6.88V16.5h33.14v26.72H23.94m21.48 0h-5.4V30.4h17.1v12.82h-4.23m4.23-12.82-6.41-8.56H40.02"/><circle class="a" cx="20.24" cy="43.76" r="3.74"/><path class="a" d="M45.42 43.23a3.82 3.82 0 1 1 0 .37v-.37"/></svg>',
      package: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-package" viewBox="0 0 64 64"><path d="M32 54.52 10 41.95v-19.9L32 9.48l22 12.57v19.9L32 54.52z" fill="none" stroke="currentColor" stroke-width="2"/><path d="M32 54.52v-19.9l22-12.57M32 34.62 10 22.05m31.7-7.03L21 28.33v8.38" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
      clock: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-clock" viewBox="0 0 64 64"><circle cx="32" cy="32" r="24" fill="none" stroke="currentColor" stroke-width="2"/><path d="M32 12v20l8 8" fill="none" stroke="currentColor" stroke-width="2"/></svg>'
    };

    const iconSvg = icons[this.settings.icon] || icons.truck;

    const flowIcons = {
      ordered: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-shopping-cart" viewBox="0 0 24 24"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/></svg>',
      processing: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-package" viewBox="0 0 64 64"><path d="M32 54.52 10 41.95v-19.9L32 9.48l22 12.57v19.9L32 54.52z" fill="none" stroke="currentColor" stroke-width="2"/><path d="M32 54.52v-19.9l22-12.57M32 34.62 10 22.05m31.7-7.03L21 28.33v8.38" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
      delivered: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-circle-checkmark" viewBox="0 0 64 64"><path d="M52.68 24.48A22 22 0 1 1 47 15.93"/><path d="m21 32 8.5 8.5L57 13" fill="none" stroke="currentColor" stroke-width="3"/></svg>'
    };

    if (this.settings.version === 2) {
      const alertText = this.settings.showAlert ? this.settings.alertText : '';
      this._buildVersion2(iconSvg, alertText);
    } else {
      this._buildVersion1(iconSvg, flowIcons);
    }
  }

  async loadHolidays() {
    // If count_holidays is disabled, no holidays to load
    if (!this.settings.includeHolidays) {
      this.holidays = [];
      return;
    }

    // Load holidays from local holidays.json file
    if (this.settings.holidayUrl) {
      try {
        const response = await fetch(this.settings.holidayUrl);
        const data = await response.json();

        // Handle both array format and object with holidays property
        if (Array.isArray(data)) {
          // Simple array of date strings
          this.holidays = data;
        } else if (data.holidays) {
          // Object format - handle @18f/us-federal-holidays format
          this.holidays = data.holidays.map(h => {
            if (typeof h === 'string') return h;
            if (h.dateString) return h.dateString;
            if (h.date) return h.date;
            return h;
          });
        } else {
          this.holidays = [];
        }

        console.log(`Loaded ${this.holidays.length} holidays from holidays.json`);
      } catch (error) {
        console.warn('Failed to load holidays:', error);
        this.holidays = [];
      }
    } else {
      this.holidays = [];
    }
  }

  getDeliveryTimes() {
    // Detect user's country/market
    const countryCode = this.detectUserCountry();

    // Find matching rule
    const rule = this.settings.marketRules.find(r =>
      r.country_code.toLowerCase() === countryCode.toLowerCase()
    );

    if (rule) {
      return {
        min: rule.delivery_min,
        max: rule.delivery_max
      };
    }

    // Return defaults
    return {
      min: this.settings.deliveryMin,
      max: this.settings.deliveryMax
    };
  }

  detectUserCountry() {
    // Try Shopify localization first
    if (window.Shopify && window.Shopify.country) {
      return window.Shopify.country;
    }

    // Try from localization object
    if (window.theme && window.theme.country) {
      return window.theme.country;
    }

    // Fallback to browser locale
    const locale = navigator.language || navigator.userLanguage;
    if (locale) {
      const parts = locale.split('-');
      if (parts.length > 1) {
        return parts[1];
      }
    }

    return 'US'; // Default
  }

  /**
   * Checks if a date is a holiday
   * @param {Temporal.ZonedDateTime} temporalDate
   * @returns {boolean}
   * @private
   */
  _isHoliday(temporalDate) {
    return this.settings.includeHolidays && this.holidays.some(holiday => temporalDate.toPlainDate().toString() === holiday);
  }

  /**
   * Checks if a date is a weekend
   * @param {Temporal.ZonedDateTime} temporalDate
   * @returns {boolean}
   * @private
   */
  _isWeekend(temporalDate) {
    return this.settings.includeWeekends && (temporalDate.dayOfWeek === 7 || temporalDate.dayOfWeek === 6);
  }

  /**
   * Offsets a date to the nearest business day
   * @param {Temporal.ZonedDateTime} referenceDate
   * @param {number} daysToAdd
   * @param {boolean} startFromSameDay
   * @returns {Temporal.ZonedDateTime}
   * @private
   */
  _offsetNearestBusinessDay(referenceDate, daysToAdd = 0, startFromSameDay = false) {
    let resultDate = referenceDate;
    let processedDays = 0;

    if (daysToAdd > 1 && startFromSameDay) {
      if (!this._isWeekend(resultDate) && !this._isHoliday(resultDate)) {
        processedDays++;
      }
    }

    while (processedDays < daysToAdd) {
      resultDate = resultDate.add({ days: 1 });

      if (!this._isWeekend(resultDate) && !this._isHoliday(resultDate)) {
        processedDays++;
      }
    }

    return resultDate;
  }

  /**
   * Gets the cutoff date and time
   * @param {Temporal.ZonedDateTime} referenceDate
   * @returns {Temporal.ZonedDateTime}
   * @private
   */
  _getCutoffDateTime(referenceDate) {
    let cutoffTime = referenceDate.with({
      hour: this.settings.cutoffHour,
      minute: this.settings.cutoffMinute,
      second: 0,
      millisecond: 0,
      microsecond: 0
    });

    if (Temporal.ZonedDateTime.compare(referenceDate, cutoffTime) !== -1) {
      do {
        cutoffTime = cutoffTime.add({ days: 1 });
      } while (
        this._isWeekend(cutoffTime) || (this._isHoliday(cutoffTime))
      )
    }

    return cutoffTime;
  }

  /**
   * Updates the delivery dates based on the current time and settings
   * @returns {import('../types').DeliveryDates} - The updated delivery dates
   */
  updateDates() {
    const now = Temporal.Now.zonedDateTimeISO(this.settings.cutoffTimezone);

    const cutoffTime = this._getCutoffDateTime(now);

    let processingStartDate;
    let processingEndDate;
    /** @var {number|null} */
    let nextCutoff = null;

    if (Temporal.ZonedDateTime.compare(cutoffTime, now) !== -1) {
      const duration = now.until(cutoffTime, { largestUnit: 'hours' });
      const absoluteHours = duration.abs().total({ unit: 'hours' });
      processingStartDate = this._offsetNearestBusinessDay(cutoffTime, this.settings.processingMin);
      processingEndDate = this._offsetNearestBusinessDay(cutoffTime, this.settings.processingMax);
      nextCutoff = absoluteHours;
    } else {
      processingStartDate = this._offsetNearestBusinessDay(processingStartDate, this.settings.processingMin);
      processingEndDate = this._offsetNearestBusinessDay(processingStartDate, this.settings.processingMax);
    }

    const deliveryTimes = this.getDeliveryTimes();

    // Calculate delivery dates
    const deliveryMinDate = this._offsetNearestBusinessDay(
      processingStartDate,
      deliveryTimes.min,
      true
    );

    const deliveryMaxDate = this._offsetNearestBusinessDay(
      processingEndDate,
      deliveryTimes.max,
      true
    );

    return {
      orderDate: now,
      cutoffTime,
      processingStartDate,
      processingEndDate,
      deliveryMinDate,
      deliveryMaxDate,
      nextCutoff,
    };
  }

  /**
   *
   * @param {Temporal.ZonedDateTime} temporalDate
   * @returns {string}
   */
  formatDateTime(temporalDate, locale = 'en-US') {
    const options = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    return temporalDate.toLocaleString(locale, options);
  }

  /**
   *
   * @param {Temporal.ZonedDateTime} temporalDate
   * @returns {string}
   */
  formatDate(temporalDate, locale = 'en-US') {
    const options = {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    };
    return temporalDate.toLocaleString(locale, options);
  }

  /**
   *
   * @param {Temporal.ZonedDateTime} temporalDate
   * @returns {string}
   */
  formatTime(temporalDate, locale = 'en-US') {
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };

    return temporalDate.toLocaleString(locale, options);
  }

  /**
   *
   * @param {Temporal.ZonedDateTime} temporalDate
   * @returns {string}
   */
  formatTimeInUserTimezone(temporalDate) {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const localDateTime = temporalDate.withTimeZone(userTimezone);

    return this.formatTime(localDateTime);
  }

  /**
   *
   * @param {Temporal.ZonedDateTime} temporalDate
   * @returns {string}
   */
  formatDateInUserTimezone(temporalDate) {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const localDateTime = temporalDate.withTimeZone(userTimezone);

    return this.formatDate(localDateTime);
  }

  /**
   * @param {Temporal.ZonedDateTime} temporalDate
   * @returns {string}
   */
  formatDateTimeInUserTimezone(temporalDate) {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const localDateTime = temporalDate.withTimeZone(userTimezone);

    return this.formatDateTime(localDateTime);
  }


  /**
   * @param {Temporal.ZonedDateTime} temporal1
   * @param {Temporal.ZonedDateTime} temporal2
   * @returns {number}
   */
  getDaysDifference(temporal1, temporal2) {
    const duration = temporal1.until(temporal2, { largestUnit: 'days' });
    const absoluteDays = duration.abs().total({ unit: 'days' });
    return Math.round(absoluteDays);
  }

  updateVersion2() {
    if (!this.widgetV2 || !this.widgetV2Output) return;

    this.dates = this.updateDates();

    const {
      deliveryMinDate,
      deliveryMaxDate
    } = this.dates;

    // const {
    //   storeLocale,
    // } = this.settings;

    const minStr = this.formatDateInUserTimezone(deliveryMinDate);
    const maxStr = this.formatDateInUserTimezone(deliveryMaxDate);

    if (minStr == maxStr) {
      this.widgetV2Output.textContent = `${minStr}`;
    } else {
      this.widgetV2Output.textContent = `${minStr} - ${maxStr}`;
    }
  }

  updateDeliveryInfo() {
    this.dates = this.updateDates();

    const {
      cutoffTime,
      deliveryMinDate,
      deliveryMaxDate
    } = this.dates;

    if (this.messageElement) {
      // Format cutoff time in user's local timezone
      const cutoffTimeStr = this.formatDateInUserTimezone(cutoffTime);
      const cutoffDateTimeStr = this.formatDateTimeInUserTimezone(cutoffTime);

      // Format delivery dates
      const minDateStr = this.formatDateInUserTimezone(deliveryMinDate);
      const maxDateStr = this.formatDateInUserTimezone(deliveryMaxDate);

      // Build message
      let message = this.settings.messageTemplate
        .replace('{cutoff_datetime}', cutoffDateTimeStr)
        .replace('{cutoff_time}', cutoffTimeStr)
        .replace('{min_date}', minDateStr)
        .replace('{max_date}', maxDateStr)
        .replace('{processing_days}', `${this.settings.processingMin}-${this.settings.processingMax}`)
        .replace('{delivery_days}', `${this.getDaysDifference(deliveryMinDate, deliveryMaxDate)}`);

      this.messageElement.innerHTML = `<span class="delivery-message__date-range">${message}</span>`;
    }


    // Update flow timeline if enabled
    if (this.settings.showFlow) {
      this.updateFlowTimeline(this.dates);
    }
  }

  /**
   * Updates the flow timeline based on the provided dates
   * @param {import('../types').DeliveryDates} dates - The delivery dates
   */
  updateFlowTimeline(dates) {
    if (!this.flowSteps) return;

    const userLocale = this.settings.storeLocale;

    // Order date - today
    const orderStep = this.querySelector('[data-step="ordered"]');

    if (orderStep) {
      const dateEl = orderStep.querySelector('.delivery-flow__step-date');
      if (dateEl) {
        dateEl.textContent = this.settings.todayLabel;
      }
    }

    // Processing date - when processing completes (show range)
    const processingStep = this.querySelector('[data-step="processing"]');

    if (processingStep) {
      const dateEl = processingStep.querySelector('.delivery-flow__step-date');
      // Calculate processing end date range
      const { processingStartDate, processingEndDate } = dates;

      const minStr = this.formatDateInUserTimezone(processingStartDate, userLocale);
      const maxStr = this.formatDateInUserTimezone(processingEndDate, userLocale);

      if (dateEl) {
        dateEl.textContent = `${minStr} - ${maxStr}`;
      }
    }

    // Delivery date - estimated delivery window
    const deliveredStep = this.querySelector('[data-step="delivered"]');

    if (deliveredStep) {
      const dateEl = deliveredStep.querySelector('.delivery-flow__step-date');
      // Show range like "Jan 15 - Jan 17"
      const { deliveryMinDate, deliveryMaxDate } = dates;

      const minStr = this.formatDateInUserTimezone(deliveryMinDate, userLocale);
      const maxStr = this.formatDateInUserTimezone(deliveryMaxDate, userLocale);

      if (dateEl) {
        dateEl.textContent = `${minStr} - ${maxStr}`;
      }
    }
  }

  startCountdown() {
    if (!this.countdownElement) return;

    const updateCountdown = () => {
      const dates = this.updateDates();

      const {
        orderDate,
        cutoffTime,
        nextCutoff
      } = this.updateDates();

      if (nextCutoff < 24) {
        const duration = orderDate.until(cutoffTime, { largestUnit: 'hours', smallestUnit: 'seconds' });
        const m = String(duration.minutes).padStart(2, '0');
        const s = String(duration.seconds).padStart(2, '0');
        let timeStr;

        if (duration.hours > 0) {
          const h = String(duration.hours).padStart(2, '0');
          timeStr = `${h}:${m}:${s}`;
        } else {
          timeStr = `${m}:${s}`;
        }

        let label = this.settings.countdownLabel.replace('{time}', timeStr);

        // Also show cutoff time in user's local timezone
        const userCutoffTime = this.formatDateTimeInUserTimezone(cutoffTime);
        label += ` (by ${userCutoffTime} your time)`;

        if (this.countdownElement) {
          this.countdownElement.innerHTML = `
            <span class="delivery-countdown__time">${label}</span>
          `;
        }

      } else {
        this.updateDeliveryInfo();
        return;
      }
    };

    updateCountdown();

    this.countdownInterval = setInterval(updateCountdown, 1000);
  }

  /**
   * @param {string} jsonString
   * @returns {import('../types').MarketRule[]}
   */
  parseMarketRules(jsonString) {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn('Failed to parse market rules:', e);
      return [];
    }
  }
}

/**
 * Estimated Delivery Widget
 * Custom element for calculating and displaying delivery dates
 */
if (!customElements.get('estimated-delivery')) {
  customElements.define('estimated-delivery', EstimatedDelivery);
}
